import { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box, Typography, Card, CardContent, CardActionArea, Chip,
  Skeleton, Stack, Fab,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField
} from '@mui/material'
import { Group, Forum, AccessTime, Add, Mail, Check, Close } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { useAuth } from '@/contexts/AuthContext'
import { apiClient } from '@/services/apiClient'
import { GroupSummary, PrayerRequest, Discussion, InAppInvite, AcceptInviteResult } from '@/types'
import { PrayerIcon } from '@/components/PrayerIcon'
import { useGroupHub } from '@/hooks/useGroupHub'
import WhatsNew from '@/components/WhatsNew'

interface GroupActivity {
  group: GroupSummary
  latestItem: { title: string; type: 'prayer' | 'discussion'; date: string } | null
  lastActivityDate: string | null
}

export default function Dashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const [groups, setGroups] = useState<GroupSummary[]>([])
  const [groupActivities, setGroupActivities] = useState<Map<string, GroupActivity>>(new Map())
  const [loading, setLoading] = useState(true)
  const [loadingActivity, setLoadingActivity] = useState(true)

  // Create group dialog state
  const [createGroupOpen, setCreateGroupOpen] = useState(false)
  const [newGroupName, setNewGroupName] = useState('')
  const [creatingGroup, setCreatingGroup] = useState(false)

  // Pending in-app invites
  const [pendingInvites, setPendingInvites] = useState<InAppInvite[]>([])
  const [loadingInvites, setLoadingInvites] = useState(true)

  // Sorted groups by most recent activity
  const sortedActivities = useMemo(() => {
    return Array.from(groupActivities.values()).sort((a, b) => {
      const aDate = a.lastActivityDate ? new Date(a.lastActivityDate).getTime() : 0
      const bDate = b.lastActivityDate ? new Date(b.lastActivityDate).getTime() : 0
      return bDate - aDate
    })
  }, [groupActivities])

  useEffect(() => {
    if (!user?.isAuthenticated) return
    const load = async () => {
      try {
        const g = await apiClient.get<GroupSummary[]>('/api/groups')
        setGroups(g)
      } catch {
        enqueueSnackbar('Failed to load data', { variant: 'error' })
      } finally {
        setLoading(false)
      }
    }
    const loadInvites = async () => {
      try {
        const inv = await apiClient.get<InAppInvite[]>('/api/invites/pending')
        setPendingInvites(inv)
      } catch {
        // Silently fail - invites are not critical
      } finally {
        setLoadingInvites(false)
      }
    }
    load()
    loadInvites()
  }, [user, enqueueSnackbar])

  // Fetch recent activity for each group
  const loadActivity = useCallback(async () => {
    if (groups.length === 0) {
      setLoadingActivity(false)
      return
    }
    setLoadingActivity(true)
    const activities = new Map<string, GroupActivity>()
    await Promise.all(
      groups.map(async (group) => {
        try {
          const [prayers, discussions] = await Promise.all([
            apiClient.get<PrayerRequest[]>(`/api/prayerrequests/group/${group.id}`),
            apiClient.get<Discussion[]>(`/api/discussions/group/${group.id}`)
          ])

          // Find the most recent item (prayer request or discussion) by createdDate
          let latestItem: GroupActivity['latestItem'] = null
          let lastActivityDate: string | null = null

          // Collect all dates (including comments via lastModifiedDate) to find last activity
          const allDates: string[] = []
          if (prayers.length > 0) {
            for (const pr of prayers) {
              allDates.push(pr.createdDate)
              if (pr.lastModifiedDate) allDates.push(pr.lastModifiedDate)
            }
          }
          if (discussions.length > 0) {
            for (const disc of discussions) {
              allDates.push(disc.createdDate)
              if (disc.lastModifiedDate) allDates.push(disc.lastModifiedDate)
            }
          }

          if (allDates.length > 0) {
            lastActivityDate = allDates.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0]
          }

          // Find the most recently created prayer or discussion for the snippet
          const latestPrayer = prayers.length > 0 ? prayers[0] : null
          const latestDiscussion = discussions.length > 0 ? discussions[0] : null

          if (latestPrayer && latestDiscussion) {
            if (new Date(latestPrayer.createdDate) >= new Date(latestDiscussion.createdDate)) {
              latestItem = { title: latestPrayer.title, type: 'prayer', date: latestPrayer.createdDate }
            } else {
              latestItem = { title: latestDiscussion.title, type: 'discussion', date: latestDiscussion.createdDate }
            }
          } else if (latestPrayer) {
            latestItem = { title: latestPrayer.title, type: 'prayer', date: latestPrayer.createdDate }
          } else if (latestDiscussion) {
            latestItem = { title: latestDiscussion.title, type: 'discussion', date: latestDiscussion.createdDate }
          }

          activities.set(group.id, { group, latestItem, lastActivityDate })
        } catch {
          activities.set(group.id, { group, latestItem: null, lastActivityDate: null })
        }
      })
    )
    setGroupActivities(activities)
    setLoadingActivity(false)
  }, [groups])

  useEffect(() => {
    loadActivity()
  }, [loadActivity])

  // Real-time updates via SignalR
  const groupIdsForHub = useMemo(() => groups.map(g => g.id), [groups])
  const refreshData = useCallback(async () => {
    const g = await apiClient.get<GroupSummary[]>('/api/groups')
    setGroups(g)
  }, [])
  useGroupHub(groupIdsForHub, {
    onPrayerRequestCreated: refreshData,
    onPrayerRequestUpdated: refreshData,
    onPrayerRequestDeleted: refreshData,
    onCommentAdded: refreshData,
    onDiscussionCreated: refreshData,
    onDiscussionUpdated: refreshData,
    onDiscussionDeleted: refreshData,
    onDiscussionCommentAdded: refreshData,
  })

  const handleAcceptInvite = async (inviteId: string) => {
    try {
      const result = await apiClient.post<AcceptInviteResult>(`/api/invites/in-app/${inviteId}/accept`)
      if (result.success) {
        enqueueSnackbar(`Joined ${result.entityName}!`, { variant: 'success' })
        setPendingInvites(prev => prev.filter(i => i.id !== inviteId))
        // Reload groups if it was a group invite
        if (result.entityType === 'group') {
          const g = await apiClient.get<GroupSummary[]>('/api/groups')
          setGroups(g)
        }
      }
    } catch {
      enqueueSnackbar('Failed to accept invite', { variant: 'error' })
    }
  }

  const handleDeclineInvite = async (inviteId: string) => {
    try {
      await apiClient.post(`/api/invites/in-app/${inviteId}/decline`)
      setPendingInvites(prev => prev.filter(i => i.id !== inviteId))
      enqueueSnackbar('Invite declined', { variant: 'info' })
    } catch {
      enqueueSnackbar('Failed to decline invite', { variant: 'error' })
    }
  }

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return
    setCreatingGroup(true)
    try {
      const group = await apiClient.post<GroupSummary>('/api/groups', { name: newGroupName.trim() })
      setCreateGroupOpen(false)
      setNewGroupName('')
      enqueueSnackbar('Group created', { variant: 'success' })
      navigate(`/groups/${group.id}`)
    } catch {
      enqueueSnackbar('Failed to create group', { variant: 'error' })
    } finally {
      setCreatingGroup(false)
    }
  }

  const formatRelativeDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }

  if (!user?.isAuthenticated) {
    return (
      <Box sx={{ textAlign: 'center', mt: 8, px: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
          ConnectLoop
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Share prayer requests with your groups and family.
        </Typography>
        <Stack direction="row" spacing={2} sx={{ justifyContent: 'center' }}>
          <Chip label="Login" onClick={() => navigate('/login')} color="primary" clickable />
          <Chip label="Register" onClick={() => navigate('/register')} variant="outlined" clickable />
        </Stack>
      </Box>
    )
  }

  return (
    <Box sx={{ pb: 10 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
        Welcome, {user.displayName}
      </Typography>

      {/* Pending Invites Section */}
      {!loadingInvites && pendingInvites.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            <Mail sx={{ verticalAlign: 'middle', mr: 1 }} />
            Pending Invites
          </Typography>
          <Stack spacing={1}>
            {pendingInvites.map(invite => (
              <Card key={invite.id} variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {invite.entityName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {invite.inviterDisplayName} invited you to join this {invite.entityType}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Button size="small" variant="contained" color="primary" startIcon={<Check />} onClick={() => handleAcceptInvite(invite.id)}>
                        Accept
                      </Button>
                      <Button size="small" variant="outlined" color="inherit" startIcon={<Close />} onClick={() => handleDeclineInvite(invite.id)}>
                        Decline
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Box>
      )}

      {/* Groups Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          <Group sx={{ verticalAlign: 'middle', mr: 1 }} />
          My Groups
        </Typography>
        {groups.length > 3 && (
          <Chip label="View All" size="small" onClick={() => navigate('/groups')} clickable />
        )}
      </Box>

      {loading || loadingActivity ? (
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={120} />
          <Skeleton variant="rounded" height={120} />
        </Stack>
      ) : groups.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary" gutterBottom>
              You haven't joined any groups yet.
            </Typography>
            <Button variant="contained" onClick={() => setCreateGroupOpen(true)}>
              Create Your First Group
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {sortedActivities.map(({ group, latestItem, lastActivityDate }) => (
            <Card key={group.id} variant="outlined" sx={{ overflow: 'visible' }}>
              <CardActionArea onClick={() => navigate(`/groups/${group.id}`)}>
                <CardContent sx={{ py: 2, px: 2.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>{group.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {group.memberCount} member{group.memberCount !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                    {group.unviewedCount > 0 && (
                      <Chip label={`${group.unviewedCount} new`} size="small" color="error" />
                    )}
                  </Box>

                  {latestItem ? (
                    <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        {latestItem.type === 'prayer' ? (
                          <PrayerIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        ) : (
                          <Forum sx={{ fontSize: 16, color: 'text.secondary' }} />
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                          Latest {latestItem.type === 'prayer' ? 'Prayer Request' : 'Discussion'}
                        </Typography>
                      </Box>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                        {latestItem.title}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No activity yet
                      </Typography>
                    </Box>
                  )}

                  {lastActivityDate && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                      <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        Last activity {formatRelativeDate(lastActivityDate)}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Stack>
      )}

      <WhatsNew />

      <Fab
        color="primary"
        aria-label="New Group"
        sx={{ position: 'fixed', bottom: 72, right: 16 }}
        onClick={() => setCreateGroupOpen(true)}
      >
        <Add />
      </Fab>

      {/* Create Group Dialog */}
      <Dialog open={createGroupOpen} onClose={() => setCreateGroupOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Create Group</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth label="Group Name" value={newGroupName}
            onChange={e => setNewGroupName(e.target.value)} margin="dense"
            onKeyDown={e => e.key === 'Enter' && handleCreateGroup()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateGroupOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateGroup} variant="contained" disabled={creatingGroup || !newGroupName.trim()}>
            {creatingGroup ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
